import { useEffect, useRef, useState } from 'react';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/modules/auth/useAuth';
import { useVerification, type VerificationStatus } from '@/modules/trust/useVerification';
import { env } from '@/config/env';
import { cn } from '@/lib/cn';

/**
 * Verification surface — loads the Persona embedded client (via a script
 * tag injected on demand), opens an inquiry pre-bound to the user's
 * auth id (passed as `referenceId`), and reflects the webhook decision
 * via `useVerification`.
 *
 * Feature flag: if `VITE_PERSONA_TEMPLATE_ID` is unset, we render a
 * placeholder explaining that verification is not yet configured. Code
 * paths around it stay green regardless.
 */
const STATUS_LABEL: Record<VerificationStatus, string> = {
  unverified: 'Not yet verified',
  pending:    'In review',
  verified:   'Verified',
  rejected:   'Did not pass — try again',
};

const STATUS_ICON: Record<VerificationStatus, string> = {
  unverified: 'pending',
  pending:    'schedule',
  verified:   'verified',
  rejected:   'error',
};

const PERSONA_SCRIPT = 'https://cdn.withpersona.com/dist/persona-v5.1.4.js';

interface PersonaClientCtor {
  new (opts: {
    templateId: string;
    environmentId?: string;
    environment?: 'sandbox' | 'production';
    referenceId: string;
    onReady?: () => void;
    onComplete?: (data: { inquiryId: string; status: string }) => void;
    onCancel?: () => void;
    onError?: (err: { code: string; message: string }) => void;
  }): { open: () => void; destroy: () => void };
}

declare global {
  interface Window { Persona?: { Client: PersonaClientCtor } }
}

const loadPersonaScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.Persona?.Client) return resolve();
    const existing = document.querySelector(`script[src="${PERSONA_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('persona script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = PERSONA_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('persona script failed'));
    document.head.appendChild(s);
  });

export const VerifyPage = () => {
  const { user } = useAuth();
  const { status, loading } = useVerification();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<{ open: () => void; destroy: () => void } | null>(null);

  const templateId = env.personaTemplateId;
  const environment = env.personaEnv;
  const configured = Boolean(templateId);

  useEffect(() => () => clientRef.current?.destroy(), []);

  const openPersona = async () => {
    if (!user || !configured) return;
    setError(null);
    setOpening(true);
    try {
      await loadPersonaScript();
      if (!window.Persona?.Client) throw new Error('persona client missing');
      clientRef.current?.destroy();
      const client = new window.Persona.Client({
        templateId: templateId!,
        environment: environment === 'production' ? 'production' : 'sandbox',
        referenceId: user.id,
        onComplete: () => setOpening(false),
        onCancel: () => setOpening(false),
        onError: (err) => {
          setError(err.message);
          setOpening(false);
        },
      });
      clientRef.current = client;
      client.open();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open verification');
      setOpening(false);
    }
  };

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the page label on the right edge. */}
          <span aria-hidden />
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Verify
          </span>
        </>
      }
    >
      <section className="border-b border-white/[0.06] px-6 py-10 md:px-12 md:py-14">
        <SectionMarker className="mb-3">Verify · identity, handled quietly</SectionMarker>
        <h1
          className="font-display font-black uppercase tracking-display"
          style={{ fontSize: 'clamp(36px, 5vw, 76px)', lineHeight: 0.9, letterSpacing: '-0.04em' }}
        >
          Verified<span className="text-pink">,</span>
          <br />
          <em className="font-light italic text-text-secondary">not surveilled</em>
          <span className="text-pink">.</span>
        </h1>
      </section>

      <div className="mx-auto max-w-3xl space-y-10 px-6 py-10 md:px-0 md:py-14">
        <section
          className={cn(
            'border p-7',
            status === 'verified'
              ? 'border-pink/40 bg-pink/[0.05] shadow-glow-soft'
              : 'border-white/[0.10] bg-charcoal/40',
          )}
        >
          <div className="flex items-center gap-4">
            <Icon name={STATUS_ICON[status]} size={28} className={status === 'verified' ? 'text-pink' : 'text-text-muted'} />
            <div>
              <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                Status
              </p>
              <p className="font-display text-xl font-black uppercase tracking-tight text-text-primary">
                {loading ? 'Loading…' : STATUS_LABEL[status]}
              </p>
            </div>
          </div>

          {(status === 'unverified' || status === 'rejected') && configured && (
            <NeonButton
              type="button"
              size="md"
              disabled={opening}
              onClick={openPersona}
              className="mt-6"
              trailing={<Icon name="arrow_forward" size={14} />}
            >
              {opening ? 'Opening' : 'Start verification'}
            </NeonButton>
          )}

          {!configured && (
            <p className="mt-4 text-xs text-text-secondary">
              Verification is not yet configured for this build. Set
              <code className="mx-1 rounded bg-charcoal/60 px-1.5 py-0.5 font-mono text-[11px] text-text-primary">VITE_PERSONA_TEMPLATE_ID</code>
              and
              <code className="mx-1 rounded bg-charcoal/60 px-1.5 py-0.5 font-mono text-[11px] text-text-primary">VITE_PERSONA_ENV</code>
              in
              <code className="mx-1 rounded bg-charcoal/60 px-1.5 py-0.5 font-mono text-[11px] text-text-primary">.env.local</code>
              to enable.
            </p>
          )}

          {error && (
            <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-pink">— {error}</p>
          )}
        </section>

        <p className="text-xs text-text-secondary">
          HOTTR stores only the decision and an opaque hash. Raw documents
          never touch our servers — Persona handles them end-to-end.
        </p>
      </div>
    </PageShell>
  );
};
